// LOCPICK1 (docs/build-briefs/LOCPICK1-franchisor-location-picker.md): the pure rules of the shared location picker. One location,
// selected locations, or all locations; a searchable list grouped by franchisee; a tri-state group; a live count. The picker only
// lists the locations the server already authorized, so nothing here widens a scope.
export interface PickerLocation {
  id: string;
  name: string;
  franchisee?: string | undefined;
  state?: string | undefined;
  /** A short muted state beside the row, for example the recipient state a publication shows on Edit. */
  note?: string | undefined;
  /** CRB5-F3: a suspended franchisee's location. Still listed and reported on, labelled, and the picker can hide it. */
  suspended?: boolean | undefined;
}
export type PickerMode = "one" | "selected" | "all";
export interface PickerValue {
  mode: PickerMode;
  ids: string[];
}
export interface PickerGroup {
  /** The franchisee heading, or null for a franchisee with one location (shown as one row, franchisee as muted text). */
  franchisee: string | null;
  locations: PickerLocation[];
}

/** The mode a stored choice reads as: all, exactly one, or some. */
export function pickerValue(allLocations: boolean, ids: readonly string[]): PickerValue {
  if (allLocations) return { mode: "all", ids: [] };
  return { mode: ids.length === 1 ? "one" : "selected", ids: [...ids] };
}
export function toFilterScope(value: PickerValue): { allLocations: boolean; locationIds: string[] } {
  return value.mode === "all" ? { allLocations: true, locationIds: [] } : { allLocations: false, locationIds: [...value.ids] };
}
export const SUSPENDED_LABEL = "Suspended";
export const HIDE_SUSPENDED_LABEL = "Hide suspended";
export function hasSuspended(locations: readonly PickerLocation[]): boolean {
  return locations.some((site) => site.suspended === true);
}
export function statesOf(locations: readonly PickerLocation[]): string[] {
  return [...new Set(locations.map((site) => site.state).filter((state): state is string => typeof state === "string" && state !== ""))].sort();
}
export function filterLocations(locations: readonly PickerLocation[], query: string, state: string, hideSuspended = false): PickerLocation[] {
  const needle = query.trim().toLowerCase();
  return locations.filter(
    (site) =>
      !(hideSuspended && site.suspended === true) &&
      (state === "" || site.state === state) &&
      (needle === "" || site.name.toLowerCase().includes(needle) || (site.franchisee ?? "").toLowerCase().includes(needle)),
  );
}
/**
 * Visible locations under their franchisee. A heading exists only for a franchisee that has more than one location in the
 * whole network list (not merely in the filtered view), so a search never turns a group into a lone row and back.
 */
export function pickerGroups(all: readonly PickerLocation[], visible: readonly PickerLocation[]): PickerGroup[] {
  const sizes = new Map<string, number>();
  for (const site of all) if (site.franchisee !== undefined) sizes.set(site.franchisee, (sizes.get(site.franchisee) ?? 0) + 1);
  const groups: PickerGroup[] = [];
  const index = new Map<string, PickerGroup>();
  for (const site of visible) {
    const grouped = site.franchisee !== undefined && (sizes.get(site.franchisee) ?? 0) > 1;
    if (!grouped) {
      groups.push({ franchisee: null, locations: [site] });
      continue;
    }
    const franchisee = site.franchisee as string;
    let group = index.get(franchisee);
    if (group === undefined) {
      group = { franchisee, locations: [] };
      index.set(franchisee, group);
      groups.push(group);
    }
    group.locations.push(site);
  }
  return groups.sort((a, b) => (a.franchisee ?? a.locations[0]?.franchisee ?? a.locations[0]?.name ?? "").localeCompare(b.franchisee ?? b.locations[0]?.franchisee ?? b.locations[0]?.name ?? ""));
}
export type GroupState = "none" | "some" | "all";
export function groupState(group: PickerGroup, ids: readonly string[]): GroupState {
  const chosen = group.locations.filter((site) => ids.includes(site.id)).length;
  return chosen === 0 ? "none" : chosen === group.locations.length ? "all" : "some";
}
export function toggleLocation(value: PickerValue, id: string): PickerValue {
  if (value.mode === "one") return { mode: "one", ids: [id] };
  return { mode: "selected", ids: value.ids.includes(id) ? value.ids.filter((existing) => existing !== id) : [...value.ids, id] };
}
/** A group on or off as a whole: any not-all state turns the whole group on, all turns it off. */
export function toggleGroup(value: PickerValue, group: PickerGroup): PickerValue {
  const ids = group.locations.map((site) => site.id);
  const next =
    groupState(group, value.ids) === "all"
      ? value.ids.filter((id) => !ids.includes(id))
      : [...value.ids, ...ids.filter((id) => !value.ids.includes(id))];
  return { mode: "selected", ids: next };
}
export function selectAll(value: PickerValue, visible: readonly PickerLocation[]): PickerValue {
  return { mode: "selected", ids: [...new Set([...value.ids, ...visible.map((site) => site.id)])] };
}
export function clearAll(): PickerValue {
  return { mode: "selected", ids: [] };
}
/** Changing the mode keeps what makes sense: One keeps the first chosen, Selected keeps the list, All forgets it. */
export function withMode(value: PickerValue, mode: PickerMode): PickerValue {
  if (mode === "all") return { mode, ids: [] };
  if (mode === "one") return { mode, ids: value.ids.slice(0, 1) };
  return { mode, ids: value.ids };
}
/** The Apply rule: One needs exactly one, Selected needs at least one, All always. */
export function canApply(value: PickerValue): boolean {
  return value.mode === "all" || (value.mode === "one" ? value.ids.length === 1 : value.ids.length > 0);
}
export function summaryText(value: PickerValue, locations: readonly PickerLocation[]): string {
  if (value.mode === "all") return "All locations";
  if (value.ids.length === 1) return locations.find((site) => site.id === value.ids[0])?.name ?? "1 location";
  return `${value.ids.length} locations`;
}
export function countText(value: PickerValue, locations: readonly PickerLocation[], plural = "locations"): string {
  return `${value.ids.length} of ${locations.length} ${plural}`;
}
/** LOCPICK1: chips show this many chosen locations before folding the rest into "+N more". */
export const CHIP_CAP = 8;
/** The inline picker hides search and the state filter for a short list, where two rows need neither. */
export const SEARCH_MIN_LOCATIONS = 8;
export function showSearch(locations: readonly PickerLocation[]): boolean {
  return locations.length >= SEARCH_MIN_LOCATIONS;
}
/** The ids a stored choice resolves to: All is every location the franchisor may target; Selected and One are the chosen ids. */
export function resolveIds(value: PickerValue, locations: readonly PickerLocation[]): string[] {
  if (value.mode === "all") return locations.map((site) => site.id);
  const offered = new Set(locations.map((site) => site.id));
  return value.ids.filter((id) => offered.has(id));
}
/** The picker value for a stored target. LOCPICK1-F1: All is a stored mode (`all`), not a reading of a full list, so a list that
 * happens to name every location today stays Selected and does not grow with the network. Never One here. */
export function valueForIds(ids: ReadonlySet<string>, locations: readonly PickerLocation[], all = false): PickerValue {
  if (all) return { mode: "all", ids: [] };
  return { mode: "selected", ids: locations.filter((site) => ids.has(site.id)).map((site) => site.id) };
}
/** The chips shown and how many fold away. */
export function chipSplit<T>(chosen: readonly T[], expanded: boolean): { shown: T[]; hidden: number } {
  if (expanded || chosen.length <= CHIP_CAP) return { shown: [...chosen], hidden: 0 };
  return { shown: chosen.slice(0, CHIP_CAP), hidden: chosen.length - CHIP_CAP };
}
